import { useState, useEffect } from "react";
import { Download, X } from "lucide-react";
import { pwaManager } from "@/utils/pwaUtils";
import { useToast } from "@/hooks/use-toast";

export function PWAInstallPrompt() {
  const [canInstall, setCanInstall] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Check if already installed
    if (pwaManager.isStandalone()) {
      return;
    }

    // Listen for install availability
    const unsubscribe = pwaManager.onInstallAvailable((installable) => {
      setCanInstall(installable);
      setIsVisible(installable);
    });

    return unsubscribe;
  }, []);

  const handleInstall = async () => {
    setIsInstalling(true);

    try {
      const success = await pwaManager.installApp();

      if (success) {
        toast({
          title: "App Installed! 🎉",
          description: "PayStreem has been added to your home screen",
        });
        setIsVisible(false);
      } else {
        toast({
          title: "Installation Cancelled",
          description: "You can install the app later from your browser menu",
          variant: "default",
        });
      }
    } catch (error) {
      toast({
        title: "Installation Failed",
        description: "There was an error installing the app. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    // Remember user dismissed the prompt
    localStorage.setItem("pwa-install-dismissed", Date.now().toString());
  };

  // Don't show if not installable, already dismissed recently, or already standalone
  if (!canInstall || !isVisible || pwaManager.isStandalone()) {
    return null;
  }

  // Check if user dismissed recently (within 7 days)
  const dismissedTime = localStorage.getItem("pwa-install-dismissed");
  if (dismissedTime) {
    const daysSinceDismissed =
      (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
    if (daysSinceDismissed < 7) {
      return null;
    }
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-[340px] z-50 rounded-[3px] border border-white/10 bg-[#17143A] p-4 font-archivo shadow-[0_18px_44px_rgba(14,12,36,0.4)]">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[3px] bg-[#5A31D8]/15">
            <Download className="h-[18px] w-[18px] text-[#A78BFA]" strokeWidth={1.8} />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-white">Install PayStreem</p>
            <p className="mt-0.5 text-[12px] text-[#9A96C4]">
              Launch from your home screen — no browser needed
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[2px] text-[#6E6B8A] hover:bg-white/5 hover:text-white transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3.5 flex gap-2">
        <button
          type="button"
          onClick={handleInstall}
          disabled={isInstalling}
          className="flex-1 h-9 rounded-[2px] bg-[#5A31D8] font-archivo text-[13px] font-bold text-white hover:bg-[#4526A8] disabled:opacity-50 transition-colors"
        >
          {isInstalling ? "Installing…" : "Install"}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className="h-9 px-4 rounded-[2px] border border-white/15 font-archivo text-[13px] font-semibold text-[#C9C5E8] hover:bg-white/5 hover:text-white transition-colors"
        >
          Later
        </button>
      </div>
    </div>
  );
}
